import { useState, useMemo, useEffect } from 'react';
import './NewEvent.css';
import { sha256 } from 'js-sha256';

export default function NewEvent({
  calendarId,
  onClose,
  onSave,
  date,
  initialEvent = null,
  mode = 'add',
  // calendarConfig
}) {
  const isEdit = mode === 'edit';
  const [cfg, setCfg] = useState();

  console.log('NewEvent render', { calendarId, date, initialEvent, mode });

  // ---- Helpers ----
  const toInputDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const normalizeHex = (c) => {
    if (!c) return null;
    let x = String(c).trim();
    if (!x.startsWith('#')) x = '#' + x;
    return /^#[0-9a-fA-F]{6}$/.test(x) ? x.toUpperCase() : null;
  };

  const uniqPushColor = (list, c) => {
    const norm = (c || '').toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(norm)) return list;
    return list.some(x => x.toUpperCase() === norm) ? list : [...list, norm];
  };

  const saveColorToPalette = (hex) => {
    const c = normalizeHex(hex);
    if (!c) return;

    // Optimistic UI update
    setPaletteColors(prev => uniqPushColor(prev, c));

    // Persist in config exportSettings.Colors
    fetch('/update_calendar_colors.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: calendarId, color: c })
    }).catch(() => {/* ignore; we already did optimistic UI */ });
  };

  const deleteColorFromPalette = (hex) => {
    const c = normalizeHex(hex);
    if (!c) return; // nothing to do

    // Optimistic UI update
    setPaletteColors(prev => prev.filter(x => x.toUpperCase() !== c));

    // Persist in config exportSettings.Colors
    fetch('/update_calendar_colors.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: calendarId, color: c })
    }).catch(() => {/* ignore; we already did optimistic UI */ });
  };

  const makeUidSync = (payload) => {
    // stabile UID ähnlich PHP makeUid: title/start/end + Entropie
    const basis =
      (payload.title || '') +
      (payload.start ? String(payload.start.toISOString ? payload.start.toISOString() : payload.start) : '') +
      (payload.end ? String(payload.end.toISOString ? payload.end.toISOString() : payload.end) : '') +
      Date.now() + Math.random();
    return sha256(basis) + '@kalendarium';
  };


  const addOneHour = (timeStr) => {
  if (!timeStr) return '09:00'; // fallback

  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h);
  d.setMinutes(m);
  d.setSeconds(0);

  d.setHours(d.getHours() + 1); // eine Stunde dazu

  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};


  // ---- when loading the config, also extract exportSettings.Colors ----
  useEffect(() => {
    if (!calendarId) {
      setCfg({ columns: [] });
      setPaletteColors([]);
      return;
    }
    fetch(`/get_calendar_config.php?id=${encodeURIComponent(calendarId)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then(j => {
        const cfgObj =
          (j && j.content && j.content.config) ||
          j.config || { columns: [] };

        const exp =
          (j && j.content && j.content.exportSettings) ||
          j.exportSettings || {};

        const colors = Array.isArray(exp.Colors) ? exp.Colors : [];
        setPaletteColors(colors.filter(c => /^#[0-9a-fA-F]{6}$/.test(String(c))).map(c => c.toUpperCase()));

        if (!Array.isArray(cfgObj.columns)) cfgObj.columns = [];

        setCfg(cfgObj);


        // Default-Startzeit aus exportSettings ziehen
        if (!isEdit && exp.DefaultStartTimeEnabled)
        {
          setStartTime(exp.DefaultStartTime || '08:00');
          setEndTime(addOneHour(exp.DefaultStartTime) || '09:00');
        }

      })
      .catch(() => {
        setCfg({ columns: [] });
        setPaletteColors([]);
      });
  }, [calendarId]);




  // ---- X-KAL State ----
  const [xkal, setXkal] = useState(() => ({ ...(initialEvent?.xkal || {}) }));
  const handleXkalChange = (key, val) => {
    const K = String(key).toUpperCase();
    setXkal(prev => ({ ...prev, [K]: val }));
  };

  // ---- Defaults je Modus ----
  const baseDate = date instanceof Date ? date : new Date();

  const defaults = useMemo(() => {
    if (isEdit && initialEvent) {
      const s = new Date(initialEvent.start);
      const e = new Date(initialEvent.end);
      return {
        id: initialEvent.id || null,
        title: initialEvent.title || '',
        description: initialEvent.description || '',
        location: initialEvent.location || '',
        startDate: toInputDate(s),
        startTime: s.toTimeString().slice(0, 5),
        endDate: toInputDate(e),
        endTime: e.toTimeString().slice(0, 5),
        recurrence: initialEvent.recurrence || 'none'
      };
    }

    const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    return {
      id: null,
      title: '',
      description: '',
      location: '',
      startDate: toInputDate(d),
      startTime: '08:00',
      endDate: toInputDate(d),
      endTime: '10:00',
      recurrence: 'none'
    };
  }, [isEdit, initialEvent, baseDate]);

  // ---- Form State ----
  const [id, setId] = useState(defaults.id);
  const [title, setTitle] = useState(defaults.title);
  const [description, setDescription] = useState(defaults.description);
  const [location, setLocation] = useState(defaults.location);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [startTime, setStartTime] = useState(defaults.startTime);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [endTime, setEndTime] = useState(defaults.endTime);
  const [recurrence, setRecurrence] = useState(defaults.recurrence);

  const [paletteColors, setPaletteColors] = useState([]);


  const [color, setColor] = useState(
    normalizeHex(initialEvent?.color) || normalizeHex(initialEvent?.xkal?.COLOR) || '#ffffff'
  );

  // ---- Save ----
  const handleSave = (e) => {
    e.stopPropagation();
    if (!title.trim()) return;

    const [sY, sM, sD] = startDate.split('-').map(Number);
    const [sH, sMin] = startTime.split(':').map(Number);
    const [eY, eM, eD] = endDate.split('-').map(Number);
    const [eH, eMin] = endTime.split(':').map(Number);

    const start = new Date(sY, sM - 1, sD, sH, sMin);
    const end = new Date(eY, eM - 1, eD, eH, eMin);
    if (start > end) {
      alert('Endzeit muss nach Startzeit liegen.');
      return;
    }

    // Farbe normalisieren & in xkal spiegeln
    const col = normalizeHex(color);
    const xkalOut = { ...xkal };

    if (col) xkalOut.COLOR = col;

    // // UID erzeugen, falls keine existiert
    const ensureId = id || makeUidSync({ title, start, end });

    const ev = {
      id: ensureId,
      title,
      description,
      location,
      start, // Date-Objekte – dein App-State wandelt später in ISO um
      end,
      recurrence,
      color: col || "#ffffff",
      xkal: xkalOut
    };

    // Komfort-Aliase (optional)
    if (xkalOut.MODULE && !ev.module) ev.module = xkalOut.MODULE;
    if (xkalOut.TEAM && !ev.team) ev.team = xkalOut.TEAM;

    if (col) saveColorToPalette(col);   // NEW: persist color used

    setId(ensureId);
    onSave(ev);
    onClose();
  };

  const close = (e) => { e.stopPropagation(); onClose(); };

  const handleDelete = (e) => {
    e.stopPropagation();
    // „Löschen“: gib Marker-Event zurück (start/end null). Parent kann dann entfernen.
    onSave({
      id: id || initialEvent?.id,
      delete: true
    });
    onClose();
  };


  // ---- X-KAL Felder aus cfg.columns (neues Format) ----
  const xkalColumns = Array.isArray(cfg?.columns)
    ? cfg.columns.filter(col => col.kind === 'xkal')
    : [];

  return (
    <div className="new-event-backdrop">
      <div className="new-event-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{isEdit ? 'Event bearbeiten' : 'Neues Event'}</h3>

        <input type="text" placeholder="Titel" value={title} onChange={e => setTitle(e.target.value)} />
        <textarea placeholder="Beschreibung" value={description} onChange={e => setDescription(e.target.value)} />
        <input type="text" placeholder="Ort" value={location} onChange={e => setLocation(e.target.value)} />

        <label>Startdatum/Uhrzeit:</label>
        <div className="row">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
        </div>

        <label>Enddatum/Uhrzeit:</label>
        <div className="row">
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
        </div>

        <label>Farbe:</label>
        <div className="color-row">
          {/* Palette swatches */}
          <div className="swatches">
            {paletteColors.map((sw, i) => (
              <button
                key={sw + i}
                type="button"
                className={`swatch ${color?.toUpperCase() === sw ? 'active' : ''}`}
                style={{ background: sw }}
                title={sw}
                onClick={() => setColor(sw)}
                aria-pressed={color?.toUpperCase() === sw}
              />
            ))}
          </div>

          {/* Native color picker (kept) */}
          <input
            type="color"
            value={color || '#FFFFFF'}
            onChange={e => setColor(e.target.value)}
            title="Eigene Farbe wählen"
          />

          {/* Save current color to palette */}
          <button
            type="button"
            className="save-color-btn"
            onClick={() => saveColorToPalette(color)}
            disabled={!normalizeHex(color)}
            title="Aktuelle Farbe zur Palette hinzufügen"
          >
            ＋
          </button>

          <button
            type="button"
            className="save-color-btn"
            onClick={() => deleteColorFromPalette(color)}
            disabled={!normalizeHex(color)}
            title="Aktuelle Farbe von Palette entfernen"
          >
            -
          </button>

        </div>

        <label>Wiederholung:</label>
        <select value={recurrence} onChange={e => setRecurrence(e.target.value)}>
          <option value="none">Keine</option>
          <option value="daily">Täglich</option>
          <option value="weekly">Wöchentlich</option>
          <option value="monthly">Monatlich</option>
          <option value="yearly">Jährlich</option>
        </select>

        {/* Dynamische X-KAL-Felder aus config.columns */}
        {xkalColumns.length > 0 && (
          <fieldset className="xkal-fields">
            <legend>Zusatzfelder</legend>
            {xkalColumns.map((f, i) => {
              const vname = (f.var || '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
              if (!vname) return null;
              const val = xkal[vname] ?? '';
              const common = {
                value: val,
                onChange: (e) => handleXkalChange(vname, e.target.value),
                required: !!f.required
              };
              return (
                <div key={`${vname}-${i}`} className="xkal-field">
                  <label>{f.label || `X-KAL-${vname}`}</label>
                  {f.type === 'select' ? (
                    <>
                      <select
                        value={val}
                        onChange={(e) => handleXkalChange(vname, e.target.value)}
                        required={!!f.required}
                      >
                        <option value="">—</option>
                        {(Array.isArray(f.options) ? f.options : []).map(opt => {
                          const obj = typeof opt === 'string' ? { value: opt, color: '#ffffff' } : opt;
                          return (
                            <option key={obj.value} value={obj.value}>
                              {obj.value}
                            </option>
                          );
                        })}
                      </select>
                      {/* optional: show color preview of selected value */}
                      {(() => {
                        const opts = Array.isArray(f.options) ? f.options : [];
                        const obj = opts
                          .map(o => (typeof o === 'string' ? { value: o, color: '#ffffff' } : o))
                          .find(o => o.value === val);
                        return obj ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 14, height: 14, borderRadius: '50%',
                              border: '1px solid #ccc', marginLeft: 8,
                              verticalAlign: 'middle', background: obj.color || '#ffffff'
                            }}
                            title={obj.color || '#ffffff'}
                          />
                        ) : null;
                      })()}
                    </>
                  )
                    : f.type === 'number' ? (
                      <input type="number" {...common} />
                    ) : f.type === 'date' ? (
                      <input type="date" {...common} />
                    ) : f.type === 'time' ? (
                      <input type="time" {...common} />
                    ) : (
                      <input type="text" {...common} />
                    )}

                </div>
              );
            })}
          </fieldset>
        )}

        <div className="buttons">
          {isEdit && <button className="delete-event-btn" onClick={handleDelete}>Entfernen</button>}
          <button onClick={close}>Abbrechen</button>
          <button onClick={handleSave}>{isEdit ? 'Aktualisieren' : 'Speichern'}</button>
        </div>
      </div>
    </div>
  );
}
